// Portfolio Administration v1.0 PR 6 — read-only right-rail showing the
// latest 30 WaterfallRun rows that used the current waterfall code. Each
// row is clickable but the drilldown drawer is a future PR (a v1 stub
// link is provided for now; immutable run detail is reachable from PR 4b
// loan-payment-tracking deep links).

import { useWaterfallRunsForCode } from "@/hooks/portfolioAdmin/usePr6";
import { Badge } from "@/design-system/components/ui";
import type { WaterfallRunSummaryDto } from "@/api/portfolioAdmin/pr6Types";
import { isoDateInTz, useDateFormatter } from "@/utils/datetime";

interface RunHistorySidebarProps {
  waterfallCode: string;
  limit?: number;
  onSelectRun?: (run: WaterfallRunSummaryDto) => void;
}

function fmtCurrency(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function fmtDate(iso: string, tz: string): string {
  return isoDateInTz(iso, tz) || "—";
}

const STATUS_VARIANT: Record<
  string,
  "draft" | "review" | "finalized" | "archived" | "error"
> = {
  draft: "draft",
  "pending-approval": "review",
  distributed: "finalized",
  approved: "finalized",
  reversed: "error",
  superseded: "archived",
};

export function RunHistorySidebar({
  waterfallCode,
  limit = 30,
  onSelectRun,
}: RunHistorySidebarProps) {
  const query = useWaterfallRunsForCode(waterfallCode, limit);
  const runs = query.data ?? [];
  const { tz } = useDateFormatter();

  return (
    <div
      className="border border-border-light rounded-md p-space-3 flex flex-col gap-space-2 bg-white-pure"
      data-testid="run-history-sidebar"
    >
      <h3 className="font-display font-bold text-h4 m-0">Run history</h3>
      {query.isLoading ? (
        <p className="text-small text-gray-mid m-0">Loading runs…</p>
      ) : runs.length === 0 ? (
        <p className="text-small text-gray-mid italic m-0">
          No runs yet for {waterfallCode}.
        </p>
      ) : (
        <ul className="flex flex-col gap-space-1 m-0 p-0 list-none">
          {runs.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => onSelectRun?.(r)}
                className="w-full text-left p-space-2 border border-border-light rounded-sm hover:border-gold-shadow flex items-center justify-between gap-space-2"
                data-testid={`run-history-row-${r.id}`}
              >
                <div className="flex flex-col">
                  <span className="text-caption text-gray-mid font-mono">
                    {fmtDate(r.runDate, tz)} · {r.collectionPeriod}
                  </span>
                  <span className="text-small font-bold">
                    {fmtCurrency(r.totalDistributed)}
                  </span>
                </div>
                <Badge status={STATUS_VARIANT[r.status] ?? "draft"}>
                  {r.status}
                </Badge>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
